import React, { ReactElement, useEffect, useState } from 'react';
import {
  Heading, Link, Loader, Tab, TabGroup,
} from 'idsk-react-components';
import { LegMaterialListItem, StadiumMaterialuEnum } from 'elegislativa-frontend-ts-axios';
import NajnovsiObsahTable from './component/NajnovsiObsahTable';
import { fetchLegislativnyMaterialList } from '../../../../client/legislativnyMaterialListClient';
import NajnovsiObsahTableItem from '../../../../model/NajnovsiObsahTableItem';
import { useClient } from '../../../../context/ClientStatusContext';
import './najnovsiObsah.scss';
import { fetchVyhlasenePredpisy } from '../../../../client/vyhlasenePredpisyListClient';
import NajnovsiObsahItem from './component/NajnovsiObsahItem';
import dateToString from '../../../../function/dateToString';
import externalRoutes from '../../../../routing/externalRoutes';
import RssLink from '../../../../page/AktualitaListPage/RssLink';
import routes from '../../../../routing/routes';

const NajnovsiObsah = (): ReactElement => {
  const {
    data: mpkProcesyList,
    status: mpkProcesyListStatus,
  } = useClient(
    () => fetchLegislativnyMaterialList(StadiumMaterialuEnum.Mpk),
  );

  const {
    data: pripravaMaterialuProcesyList,
    status: pripravaMaterialuProcesyListStatus,
  } = useClient(
    () => fetchLegislativnyMaterialList(StadiumMaterialuEnum.Vyjadrenia),
  );

  const {
    data: vyhlasenePredpisyList,
    status: vyhlasenePredpisyListStatus,
  } = useClient(
    () => fetchVyhlasenePredpisy(),
  );

  const mapToNajnovsiObsahTableItem = (data: LegMaterialListItem): NajnovsiObsahTableItem => ({
    id: data.uuid,
    cislo: data.cisloLegislativnehoMaterialu,
    typ: data.typ?.nazov,
    nazov: data.nazov,
    vyhlaseny: data.zaciatokStadia ? new Date(data.zaciatokStadia) : undefined,
    redirectUrl: `/elegislativa/legislativne-procesy/SK/${data.cisloLegislativnehoMaterialu}`,
  });

  const reformatZaciatokStadia = (inputDate: string): string => {
    const [year, month, day] = inputDate.split('-');
    return `${day}.${month}.${year}`;
  };

  const neexistujuceZaznamy = (): ReactElement => (
    <p className="text-center">Nenašli sa žiadne
      záznamy.
    </p>
  );

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <Heading className="najnovsi-obsah-heading" size="l">Najnovší obsah</Heading>
      <TabGroup className="najnovsi-obsah-tab-group">
        <Tab id="nove-vyhlasene-predpisy-tab" title="Nové vyhlásené predpisy">
          <Loader loading={vyhlasenePredpisyListStatus.loading}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {vyhlasenePredpisyList && vyhlasenePredpisyList.length > 0
              ? (
                isMobile ? (
                  <div>
                    {vyhlasenePredpisyList.map(item => (
                      <NajnovsiObsahItem
                        firstColumn={(
                          <>
                            <div>{item.cislo}</div>
                          </>
                      )}
                        secondColumn={(
                          <>
                            <div className="vyhlaseny-datum">
                              {item.vyhlaseny ? dateToString(item.vyhlaseny, false) : undefined}
                            </div>
                          </>
                      )}
                        content={item?.nazov}
                        url={item?.redirectUrl}
                        key={`najnovsi-obsah-vyhlasene-predpisy-${item.id}`}
                        id={`najnovsi-obsah-vyhlasene-predpisy-${item.id}`}
                      />
                    ))}
                    <div className="najnovsi-obsah-links-container">
                      <div className="najnovsi-obsah-link-rss">
                        <RssLink to={externalRoutes.rss.predpisZbierky} />
                      </div>
                      <div className="najnovsi-obsah-link-content">
                        <Link to={routes.vyhladavaniePravnychPredpisov.to}>
                          {routes.vyhladavaniePravnychPredpisov.name}
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <NajnovsiObsahTable
                      data={vyhlasenePredpisyList}
                    />
                    <div className="najnovsi-obsah-links-container">
                      <div className="najnovsi-obsah-link-rss">
                        <RssLink to={externalRoutes.rss.predpisZbierky} />
                      </div>
                      <div>
                        <Link to={routes.vyhladavaniePravnychPredpisov.to}>
                          {routes.vyhladavaniePravnychPredpisov.name}
                        </Link>
                      </div>
                    </div>
                  </>
                )
              )
              : neexistujuceZaznamy()}
          </Loader>
        </Tab>
        <Tab id="mpk-procesy-tab" title="Procesy - pripomienkové konania">
          <Loader loading={mpkProcesyListStatus.loading}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {mpkProcesyList && mpkProcesyList.length > 0 ? (
              isMobile ? (
                <>
                  <div>
                    {mpkProcesyList?.map(item => (
                      <NajnovsiObsahItem
                        firstColumn={(
                          <>
                            <div>{item.typ?.nazov}</div>
                            <div>{item.cisloLegislativnehoMaterialu}</div>
                          </>
                    )}
                        secondColumn={(
                          <>
                            <div className="zaciatok-stadia">Začiatok štádia</div>
                            <div>{reformatZaciatokStadia(item.zaciatokStadia)}</div>
                          </>
                    )}
                        content={item?.nazov}
                        url={item?.cisloLegislativnehoMaterialu
                          ? `/elegislativa/legislativne-procesy/SK/${item?.cisloLegislativnehoMaterialu}` : ''}
                        key={`najnovsi-obsah-pk-procesy-${item.uuid}`}
                        id={`najnovsi-obsah-pk-procesy-${item.uuid}`}
                      />
                    ))}
                    <div className="najnovsi-obsah-links-container">
                      <div className="najnovsi-obsah-link-rss">
                        <RssLink to={externalRoutes.rss.legislativnyMaterial} />
                      </div>
                      <div className="najnovsi-obsah-link-content">
                        <Link to={routes.legislativneProcesy.to}>
                          {routes.legislativneProcesy.name}
                        </Link>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <NajnovsiObsahTable
                    data={mpkProcesyList?.map(mpkProces => mapToNajnovsiObsahTableItem(mpkProces))}
                    showTyp
                    proces
                  />
                  <div className="najnovsi-obsah-links-container">
                    <div className="najnovsi-obsah-link-rss">
                      <RssLink to={externalRoutes.rss.legislativnyMaterial} />
                    </div>
                    <div>
                      <Link to={routes.legislativneProcesy.to}>
                        {routes.legislativneProcesy.name}
                      </Link>
                    </div>
                  </div>
                </>
              )
            ) : (
              neexistujuceZaznamy()
            )}
          </Loader>
        </Tab>
        <Tab id="predbezne-informacie-tab" title="Predbežné informácie - vyjadrenia">
          <Loader loading={pripravaMaterialuProcesyListStatus.loading}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {pripravaMaterialuProcesyList && pripravaMaterialuProcesyList.length > 0
              ? (
                isMobile ? (
                  <div>
                    {pripravaMaterialuProcesyList.map(item => (
                      <NajnovsiObsahItem
                        firstColumn={(
                          <>
                            <div>{item.cisloLegislativnehoMaterialu}</div>
                          </>
                                )}
                        secondColumn={(
                          <>
                            <div className="zaciatok-stadia">Začiatok štádia</div>
                            <div>{reformatZaciatokStadia(item.zaciatokStadia)}</div>
                          </>
                                )}
                        content={item?.nazov}
                        url={item?.cisloLegislativnehoMaterialu
                          ? `/elegislativa/legislativne-procesy/SK/${item?.cisloLegislativnehoMaterialu}` : ''}
                        key={`najnovsi-obsah-priprava-materialu-procesy-${item.uuid}`}
                        id={`najnovsi-obsah-priprava-materialu-procesy-${item.uuid}`}
                      />
                    ))}
                    <div className="najnovsi-obsah-links-container">
                      <div className="najnovsi-obsah-link-content">
                        <Link to={routes.predbezneInformacie.to}>
                          {routes.predbezneInformacie.name}
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <NajnovsiObsahTable
                      data={pripravaMaterialuProcesyList?.map(mpkProces => mapToNajnovsiObsahTableItem(mpkProces))}
                      proces
                    />
                    <div className="najnovsi-obsah-links-container">
                      <div>
                        <Link to={routes.predbezneInformacie.to}>
                          {routes.predbezneInformacie.name}
                        </Link>
                      </div>
                    </div>
                  </>
                )
              )
              : neexistujuceZaznamy()}
          </Loader>
        </Tab>
      </TabGroup>
    </>
  );
};

export default NajnovsiObsah;
